import { memo, useCallback, useEffect, useState, ChangeEvent } from 'react';
import { unstable_batchedUpdates as batch } from 'react-dom';
import * as MiddlewareService from '@root/lib/services/MiddlewareService';
import useAsyncCallback from '@root/lib/hooks/useAsyncCallback';
import Modal from '@root/lib/components/Modal';
import Close from '@root/lib/components/Icons/Close';
import Button from '@root/lib/components/Button';
import cn from 'classnames';
import './ZipModal.scss';

/**
 * @param {{
 *   zip: string|null;
 *   open: boolean;
 *   setRegion: (region: {
 *     zipcode: string;
 *     zone: string;
 *     transit_time: string;
 *     transit_days: number;
 *   }) => void;
 *   onClose: () => void;
 * }} params
 */
const ZipModal = ({
  zip,
  setRegion,
  open,
  onClose,
}) => {
  const [ localZip, setLocalZip ] = useState(zip ?? '');
  const [ error, setError ] = useState('');
  const [ checking, setChecking ] = useState(false);

  /**
   * Handles the dismissal of the modal. If the zip provided to this component
   * is not set this function will now allow the modal to close.
   */
  const handleDismiss = useCallback(() => {
    if (!zip) return;
    setLocalZip(zip);
    onClose();
  }, [zip, onClose]);

  /**
   * Handles the change event for the zip input
   *
   * @type {(e: ChangeEvent<HTMLInputElement>) => void}
   */
  const handleChangeZip = useCallback((e) => {
    const value = e.target.value;
    // Not changing the zip if the zip is not a number or it ends with e. Have to check for
    // e because strings like "123e" are consided numbers for some reason
    if (isNaN(e.target.value) || value.endsWith('e')) return;
    setError('');
    setLocalZip(e.target.value);
  }, []);

  /**
   * Checks
   *
   * @type {(e: React.FormEvent<HTMLFormElement>) => void}
   */
  const handleSubmit = useAsyncCallback(async (isCancelled, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Closes the modal if the zip saves was the same zip that was passed to this component
    if (zip === localZip) {
      onClose();
      return;
    }

    try {
      setChecking(true);
      const region = await MiddlewareService.getRegion(localZip);

      !isCancelled() && batch(() => {
        setRegion(region);
        setChecking(false);
      });

    } catch (e) {
      !isCancelled() && batch(() => {
        setChecking(false);
        const error = e.response?.data?.error;
        
        // Checking if it's an application error
        if (error !== undefined) {
          if (error === 'region_not_found') {
            setError('Sorry, we don\'t service that zip code.  Please enter another zip code to view pricing.');
            return;
          }

          setError(`UNHANDLED_ERROR: ${error}`);
          return;
        }

        setError('Something went wrong validating zip. Please try again');
      });
    }
  }, [localZip, setRegion]);

  // Setting the local zip to the prop zip when the prop zip changes
  useEffect(() => {
    setLocalZip(zip ?? '');
  }, [zip]);

  const language = {
    heading: Shopify.language.zip_modal.my_choice.heading,
    subtext: Shopify.language.zip_modal.my_choice.subtext,
    button_cta: Shopify.language.zip_modal.my_choice.button_cta
  };

  if ( window.location.pathname.startsWith( "/products" ) ) {
    const productHandle = window.location.pathname.split( '/' )[2];

    if ( productHandle.startsWith( "chefs-choice" ) ) {
      language.heading = Shopify.language.zip_modal.chefs_choice.heading;
      language.subtext = Shopify.language.zip_modal.chefs_choice.subtext;
      language.button_cta = Shopify.language.zip_modal.chefs_choice.button_cta;
    }
  }

  return (
    <Modal open={open} onDismiss={handleDismiss}>
      <div className="ZipModal">
        {zip && (
          <Close onClick={handleDismiss} className="ZipModal__Dismiss" />
        )}
        <div>
          <h2 className="ZipModal__Header">{ language.heading }</h2>
          <p className="ZipModal__Copy">{ language.subtext }</p>
        </div>
        <div>
          <form onSubmit={handleSubmit} action="/" method="POST" className="ZipModal__Form">
            <div className="ZipModal__InputGroup">
              <div className={cn('ZipModal__InputWrapper', {
                'ZipModal__InputWrapper--error': !!error,
                'ZipModal__InputWrapper--loading': checking,
              })}>
                <input
                  disabled={checking}
                  className={cn('ZipModal__Input', {
                    'ZipModal__Input--error': !!error,
                  })}
                  placeholder="Zip Code"
                  maxLength={5}
                  type="text"
                  value={localZip}
                  onChange={handleChangeZip}
                />
              </div>
              <Button
                type="submit"
                className="ZipModal__Save"
                disabled={localZip.length !== 5 || checking}
                children={language.button_cta}
              />
            </div>
            {error && <p className="ZipModal__Copy ZipModal__Copy--error">{error}</p>}
          </form>
          <p className="ZipModal__LoginCopy">
            Already have an account? <a href="/account/login">Log in</a>
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default memo(ZipModal);
